import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, html } from 'lit';
import { state, query, property, customElement } from 'lit/decorators.js';
import { unsafeStatic, html as html$1 } from 'lit/static-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation$2 = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation$2);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

/**
 * Returns the nearest focusable ancestor of `el` by walking up parentElement.
 * Used as the last-resort fallback when a self-removing component (chip, alert)
 * has no adjacent sibling of the same type to shift focus to.
 *
 * "Focusable ancestor" means an element with tabindex >= 0. The container that
 * wraps a chip group or alert list is the expected target; authors following the
 * WAI-ARIA pattern should give it tabindex="0".
 */
function focusableFallbackAncestor(el) {
    let current = el.parentElement;
    while (current) {
        const tab = current.getAttribute('tabindex');
        if (tab !== null && parseInt(tab, 10) >= 0)
            return current;
        current = current.parentElement;
    }
    return null;
}
/** Mirrors FocusTrapController's notion of "focusable", scoped to the handful of
 *  interactive elements consumers actually place next to alert/chip/notification. */
const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'it-button:not([disabled])',
].join(',');
/**
 * True last-resort fallback for when a self-removing component (alert, chip,
 * notification) has neither an adjacent sibling of the same type nor a
 * `focusableFallbackAncestor` (i.e. no author-provided `tabindex="0"` wrapper) —
 * the common case in practice, since most real usages don't add that wrapper.
 * Without this, focus silently drops to `<body>` with no way for a keyboard/AT
 * user to tell where they ended up.
 *
 * Finds the closest focusable element to `el` anywhere in the document, walking
 * outward in document order: the nearest one that comes before `el` (closer to
 * where the user's attention already was), falling back to the nearest one after.
 * This is a coarse, page-wide search — it may land focus on something not
 * visually adjacent — but it is deterministic and strictly better than silently
 * losing focus.
 */
function nearestFocusableInDocument(el) {
    const candidates = Array.from(document.querySelectorAll(FOCUSABLE_SELECTORS)).filter((candidate) => candidate !== el && !el.contains(candidate));
    if (!candidates.length)
        return null;
    let closestBefore = null;
    let closestAfter = null;
    candidates.forEach((candidate) => {
        // eslint-disable-next-line no-bitwise
        if (el.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_PRECEDING) {
            closestBefore = candidate; // querySelectorAll returns document order, so the last match wins
        }
        else if (!closestAfter) {
            closestAfter = candidate; // first match after `el` in document order
        }
    });
    return closestBefore ?? closestAfter;
}

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

const NOTIFICATION_HEADING_LEVELS = ['h2', 'h3', 'h4', 'h5', 'h6'];
const NOTIFICATION_STATUS = ['success', 'error', 'warning', 'info'];
const NOTIFICATION_STATUS_COLORS = {
    success: 'success',
    error: 'danger',
    warning: 'warning',
    info: 'primary',
};
const NOTIFICATION_FIX = ['top', 'bottom', 'left', 'right'];

var styles = css`@charset "UTF-8";
.fade {
  transition: opacity 0.15s linear;
}
@media (prefers-reduced-motion: reduce) {
  .fade {
    transition: none;
  }
}
.fade:not(.show) {
  opacity: 0;
}

.collapse:not(.show) {
  display: none;
}

.collapsing {
  height: 0;
  overflow: hidden;
  transition: height 0.35s ease;
}
@media (prefers-reduced-motion: reduce) {
  .collapsing {
    transition: none;
  }
}
.collapsing.collapse-horizontal {
  width: 0;
  height: auto;
  transition: width 0.35s ease;
}
@media (prefers-reduced-motion: reduce) {
  .collapsing.collapse-horizontal {
    transition: none;
  }
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.notification {
  --bsi-notification-background: var(--bsi-color-background-inverse);
  --bsi-notification-border-color: var(--bsi-color-border-subtle);
  --bsi-notification-box-shadow: var(--bsi-elevation-medium);
  --bsi-notification-inset-line-width: var(--bsi-border-thick);
  --bsi-notification-inset-line-color: var(--bsi-color-border-primary);
  --bsi-notification-padding-x: var(--bsi-spacing-s);
  --bsi-notification-padding-y: var(--bsi-spacing-s);
  --bsi-notification-padding-right: calc(var(--bsi-spacing-m) + var(--bsi-spacing-xl));
  --bsi-notification-position-distance: var(--bsi-spacing-xxs);
  --bsi-notification-title-color: var(--bsi-color-text-base);
  --bsi-notification-title-font-size: var(--bsi-heading-6-font-size);
  --bsi-notification-text-color: var(--bsi-color-text-secondary);
  --bsi-notification-text-font-size: var(--bsi-label-font-size);
}
@media (min-width: 768px) {
  .notification {
    --bsi-notification-position-distance: var(--bsi-spacing-s);
  }
}

/* stylelint-disable-next-line no-duplicate-selectors */
.notification {
  position: fixed;
  right: var(--bsi-notification-position-distance);
  bottom: var(--bsi-notification-position-distance);
  display: none;
  width: calc(100% - var(--bsi-notification-position-distance) * 2);
  padding: var(--bsi-notification-padding-y) var(--bsi-notification-padding-x);
  border: 1px solid var(--bsi-notification-border-color);
  box-shadow: inset var(--bsi-notification-inset-line-width) 0 0 var(--bsi-notification-inset-line-color), var(--bsi-notification-box-shadow);
  border-radius: var(--bsi-radius-smooth);
  background: var(--bsi-notification-background);
}
@media (min-width: 768px) {
  .notification {
    width: 33vw;
    max-width: 300px;
  }
  .notification.top-fix {
    top: var(--bsi-notification-position-distance);
    right: auto;
    bottom: auto;
    left: 50%;
    transform: translateX(-50%);
  }
  .notification.bottom-fix {
    right: auto;
    bottom: var(--bsi-notification-position-distance);
    left: 50%;
    transform: translateX(-50%);
  }
  .notification.left-fix {
    top: 50%;
    right: auto;
    bottom: auto;
    left: var(--bsi-notification-position-distance);
    border-left: none;
    transform: translateY(-50%);
  }
  .notification.right-fix {
    top: 50%;
    right: var(--bsi-notification-position-distance);
    bottom: auto;
    transform: translateY(-50%);
  }
}
@media (min-width: 992px) {
  .notification {
    width: 25vw;
    max-width: 375px;
  }
}
.notification-title {
  position: relative;
  margin: 0;
  color: var(--bsi-notification-title-color);
  font-size: var(--bsi-notification-title-font-size);
  font-weight: var(--bsi-font-weight-strong);
  line-height: var(--bsi-heading-leading);
}
.notification p {
  margin-top: var(--bsi-spacing-xxs);
  color: var(--bsi-notification-text-color);
  font-size: var(--bsi-notification-text-font-size);
  line-height: var(--bsi-body-leading);
}
.notification p:last-child {
  margin-bottom: 0;
}
.notification.with-icon .notification-title {
  display: flex;
  align-items: center;
}
.notification.with-icon p {
  margin-bottom: 0;
  margin-left: var(--bsi-spacing-l);
}
.notification.with-icon.success {
  --bsi-notification-inset-line-color: var(--bsi-color-border-success);
  --bsi-notification-icon-fill: var(--bsi-icon-success);
}
.notification.with-icon.error {
  --bsi-notification-inset-line-color: var(--bsi-color-border-danger);
  --bsi-notification-icon-fill: var(--bsi-icon-danger);
}
.notification.with-icon.info {
  --bsi-notification-inset-line-color: var(--bsi-color-border-secondary);
  --bsi-notification-icon-fill: var(--bsi-icon-secondary);
}
.notification.with-icon.warning {
  --bsi-notification-inset-line-color: var(--bsi-color-border-warning);
  --bsi-notification-icon-fill: var(--bsi-icon-warning);
}
.notification.with-icon.success .icon, .notification.with-icon.error .icon, .notification.with-icon.info .icon, .notification.with-icon.warning .icon {
  fill: var(--bsi-notification-icon-fill);
  margin-right: var(--bsi-spacing-xxs);
}
.notification.dismissable .notification-close {
  position: absolute;
  top: var(--bsi-notification-padding-y);
  right: var(--bsi-notification-padding-x);
  display: block;
  padding: 0;
  background: none;
}
.notification.dismissable .notification-close .icon {
  fill: var(--bsi-icon-secondary);
}
.notification.dismissable .notification-close:hover .icon {
  fill: var(--bsi-icon-primary);
}
.notification.dismissable .notification-close:active {
  box-shadow: none;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

body {
  font-family: var(--bsi-font-sans);
}

p,
ul,
ol,
dl {
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}

caption,
figcaption {
  font-size: var(--bsi-caption-font-size);
  line-height: var(--bsi-caption-leading);
}

b,
strong {
  font-weight: var(--bsi-font-weight-strong);
}

mark,
code {
  padding: 0 var(--bsi-spacing-3xs);
}

mark {
  background-color: var(--bsi-highlight-background);
}

pre,
code,
kbd,
samp {
  border-radius: var(--bsi-radius-smooth);
  font-family: var(--bsi-font-mono);
}

ins,
del {
  position: relative;
  display: flex;
  align-items: center;
  padding: var(--bsi-spacing-3xs) var(--bsi-spacing-m);
  font: var(--bsi-body-font-size);
  text-decoration: none;
}

ins {
  background-color: var(--bsi-ins-background);
  text-decoration: none;
}

del {
  background-color: var(--bsi-del-background);
}

del::before,
ins::before {
  position: absolute;
  left: 0.5rem;
}

del::before {
  content: "−";
}

ins::before {
  content: "+";
}

kbd {
  margin: 0 2px;
  padding: 2px var(--bsi-spacing-3xs);
  border: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  background-color: var(--bsi-color-background-secondary-lighter);
  box-shadow: var(--bsi-elevation-low), 0 2px 0 0 hsla(255, 0%, 100%, 0.7) inset;
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-font-size-1);
  font-weight: var(--bsi-font-weight-solid);
  white-space: nowrap;
}
kbd kbd {
  padding: 0;
  font-size: var(--bsi-code-font-size);
}

small,
.small {
  font-size: var(--bsi-caption-font-size);
}

.x-small {
  font-size: var(--bsi-font-size-1);
}

h1,
.h1 {
  font-size: var(--bsi-heading-1-font-size);
}

h2,
.h2 {
  font-size: var(--bsi-heading-2-font-size);
}

h3,
.h3 {
  font-size: var(--bsi-heading-3-font-size);
}

h4,
.h4 {
  font-size: var(--bsi-heading-4-font-size);
}

h5,
.h5 {
  font-size: var(--bsi-heading-5-font-size);
}

h6,
.h6 {
  font-size: var(--bsi-heading-6-font-size);
}

h1,
.h1,
h2,
.h2,
h3,
.h3,
h4,
.h4,
h5,
.h5,
h6,
.h6 {
  margin-top: 0;
  margin-bottom: var(--bsi-heading-spacing);
  line-height: var(--bsi-heading-leading);
}
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h1, p + h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h1,
p + .h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h2,
p + h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h2,
p + .h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h3,
p + h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h3,
p + .h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h4,
p + h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h4,
p + .h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h5,
p + h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h5,
p + .h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h6,
p + h6,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h6,
p + .h6 {
  padding-top: var(--bsi-spacing-s);
}

h1,
h2,
h3,
.h1,
.h2,
.h3 {
  font-weight: var(--bsi-heading-font-weight);
}

h4,
h5,
h6,
.h4,
.h5,
.h6 {
  font-weight: var(--bsi-heading-font-weight-weak);
}

h1,
.h1,
.display-1 {
  letter-spacing: -1px;
}

.font-serif {
  font-family: var(--bsi-font-serif) !important;
}

.font-sans-serif {
  font-family: var(--bsi-font-sans) !important;
}

.font-monospace {
  font-family: var(--bsi-font-mono) !important;
}

.display-1 {
  font-size: var(--bsi-display-font-size);
  font-weight: var(--bsi-heading-font-weight);
  line-height: var(--bsi-heading-leading);
}

.lead {
  font-size: var(--bsi-lead-font-size);
  font-weight: var(--bsi-lead-font-weight);
  line-height: var(--bsi-lead-leading);
}

blockquote,
.blockquote {
  margin: var(--bsi-spacing-m) 0;
  margin-left: var(--bsi-spacin-inline-xs);
  padding: var(--bsi-spacing-s);
  border-left: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}
blockquote > :last-child,
.blockquote > :last-child {
  margin-bottom: 0;
}
blockquote.text-end,
.blockquote.text-end {
  margin-right: var(--bsi-spacin-inline-xs);
  border-right: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
}
blockquote.text-center, blockquote.text-end, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.text-end,
.blockquote.blockquote-simple {
  margin-left: 0;
  border-right: none;
  border-left: none;
}
blockquote.text-center, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.blockquote-simple {
  padding: 0;
}
blockquote.blockquote-simple,
.blockquote.blockquote-simple {
  font-style: italic;
}
blockquote.blockquote-card,
.blockquote.blockquote-card {
  margin-left: 0;
  padding: var(--bsi-spacing-m);
  background-color: var(--bsi-color-background-inverse);
  box-shadow: var(--bsi-elevation-low);
}
blockquote.blockquote-card .blockquote-footer,
.blockquote.blockquote-card .blockquote-footer {
  font-size: inherit;
}
blockquote.blockquote-card .blockquote-footer:before,
.blockquote.blockquote-card .blockquote-footer:before {
  content: none;
}
blockquote.blockquote-card.dark,
.blockquote.blockquote-card.dark {
  border-left: none;
  background-color: var(--bsi-color-background-primary);
  color: var(--bsi-color-text-inverse);
}
blockquote.blockquote-card.dark .blockquote-footer,
.blockquote.blockquote-card.dark .blockquote-footer {
  color: var(--bsi-color-text-inverse);
}

.blockquote-footer {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-label-font-size);
}
.blockquote-footer::before {
  content: "— ";
}
.bg-dark .blockquote-footer {
  color: var(--bsi-color-text-muted);
}

.initialism {
  font-size: var(--bsi-label-font-size-s);
  text-transform: uppercase;
}

.me-2 {
  margin-right: 0.5rem !important;
}

@media (min-width: 576px) {
  .me-sm-2 {
    margin-right: 0.5rem !important;
  }
}
@media (min-width: 768px) {
  .me-md-2 {
    margin-right: 0.5rem !important;
  }
}
@media (min-width: 992px) {
  .me-lg-2 {
    margin-right: 0.5rem !important;
  }
}
@media (min-width: 1200px) {
  .me-xl-2 {
    margin-right: 0.5rem !important;
  }
}
@media (min-width: 1400px) {
  .me-xxl-2 {
    margin-right: 0.5rem !important;
  }
}
/**
 * Copied from bsi reboot
 */
*,
*::before,
*::after {
  box-sizing: border-box;
}

/* END copied from bsi reboot */
.notification {
  z-index: var(--bsi-notification-z-index, 2);
}

::slotted(p) {
  margin-top: var(--bsi-spacing-xxs) !important;
  color: var(--bsi-notification-text-color);
  font-size: var(--bsi-notification-text-font-size) !important;
  line-height: var(--bsi-body-leading) !important;
}

::slotted(p:last-child) {
  margin-bottom: 0 !important;
}

.with-icon ::slotted(p) {
  margin-bottom: 0 !important;
  margin-left: var(--bsi-spacing-l);
}

.notification-close {
  --bsi-icon-default: hsl(210, 17%, 44%);
  border: 0;
}
.notification-close:hover {
  --bsi-icon-default: hsl(210, 100%, 40%);
}`;

const translation$1 = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    // Close button label
    notification_close_label: 'Chiudi notifica',
};

const translation = {
    $code: 'en',
    $name: 'English',
    $dir: 'ltr',
    // Close button label
    notification_close_label: 'Close notification',
};

var ItNotification_1;
registerTranslation(translation$1);
registerTranslation(translation);
/**
 * Componente Notification.
 *
 * @element it-notification
 *
 * @fires it-notification-show - Quando la notifica viene mostrata
 * @fires it-notification-close - Quando la notifica viene chiusa (click sul pulsante di chiusura, scomparsa automatica o chiamata a `hide()`)
 */
let ItNotification = ItNotification_1 = class ItNotification extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.headingLevel = 'h2';
        this.dismissable = false;
        this.fade = true;
        this.timeout = 3000;
        this.isShown = false;
        this.isTransitioning = false;
        /** Element focused right before `show()` was called (e.g. the button that triggered it).
         *  Restored on `hide()` if focus was left inside the notification (e.g. its close button),
         *  since there is no author-provided trigger slot to fall back on like `it-modal` has. */
        this._returnFocusTarget = null;
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (this.headingLevel && !NOTIFICATION_HEADING_LEVELS.includes(this.headingLevel)) {
            this.logger.warn(`Invalid heading-level value, falling back to default. Expected one of: ${NOTIFICATION_HEADING_LEVELS.join(', ')}`);
        }
        if (this.status && !NOTIFICATION_STATUS.includes(this.status)) {
            this.logger.warn(`Invalid status value, expected one of: ${NOTIFICATION_STATUS.join(', ')}`);
        }
        if (this.fix && !NOTIFICATION_FIX.includes(this.fix)) {
            this.logger.warn(`Invalid fix value, expected one of: ${NOTIFICATION_FIX.join(', ')}`);
        }
    }
    show(timeout) {
        if (this.isShown || this.isTransitioning) {
            return;
        }
        this.isShown = true;
        if (this.fade)
            this.isTransitioning = true;
        const previouslyFocused = document.activeElement;
        this._returnFocusTarget =
            previouslyFocused && previouslyFocused !== document.body && previouslyFocused !== this
                ? previouslyFocused
                : null;
        const timeoutVal = timeout ?? this.timeout;
        this.container.style.display = 'block';
        if (this.fade) {
            /**
             * Trick to restart an element's animation
             * @see https://www.charistheo.io/blog/2021/02/restart-a-css-animation-with-javascript/#restarting-a-css-animation
             */
            // eslint-disable-next-line no-unused-expressions
            this.offsetHeight;
        }
        this.isShown = true;
        // Inject the flattened slot text into the dedicated live region so that
        // NVDA/JAWS announce the notification (they do not resolve slotted content
        // across the shadow boundary at announcement time).
        this.updateComplete.then(() => this.announce());
        this.dispatchEvent(new CustomEvent('it-notification-show', {
            bubbles: true,
            composed: true,
            detail: { notification: this },
        }));
        setTimeout(() => {
            this.isTransitioning = false;
            if (!this.dismissable && timeoutVal) {
                setTimeout(() => {
                    this.hide();
                }, timeoutVal);
            }
        }, this.fade ? ItNotification_1.TRANSITION_DURATION : 0);
    }
    hide() {
        if (!this.isShown || this.isTransitioning) {
            return;
        }
        this.isShown = false;
        if (this.fade)
            this.isTransitioning = true;
        this.dispatchEvent(new CustomEvent('it-notification-close', {
            bubbles: true,
            composed: true,
            detail: { notification: this },
        }));
        setTimeout(() => {
            // The container is about to become display:none, which would otherwise silently
            // drop focus to <body> if it currently holds it (e.g. right after a click on the
            // close button). Restore it to whatever had focus before show() (e.g. the button
            // that triggered it) — there is no author-provided trigger slot to target like
            // it-modal has. Fall back to the nearest focusable ancestor (the it-alert/it-chip
            // convention) if that element is gone.
            if (this.shadowRoot?.activeElement) {
                const target = this._returnFocusTarget && this._returnFocusTarget.isConnected
                    ? this._returnFocusTarget
                    : (focusableFallbackAncestor(this) ?? nearestFocusableInDocument(this));
                target?.focus();
            }
            this._returnFocusTarget = null;
            this.container.style.display = 'none';
            // Clear the live region so a subsequent show() re-announces even when
            // the content is identical.
            this.liveRegion.textContent = '';
            this.isTransitioning = false;
        }, this.fade ? ItNotification_1.TRANSITION_DURATION : 0);
    }
    getHeadingLevel() {
        if (NOTIFICATION_HEADING_LEVELS.includes(this.headingLevel)) {
            return this.headingLevel;
        }
        return 'h2';
    }
    getSlotText(name) {
        const selector = name ? `slot[name="${name}"]` : 'slot:not([name])';
        const slot = this.shadowRoot?.querySelector(selector);
        return (slot?.assignedNodes({ flatten: true }) ?? [])
            .map((node) => node.textContent?.trim() ?? '')
            .filter(Boolean)
            .join(' ')
            .trim();
    }
    announce() {
        this.liveRegion.textContent = [this.getSlotText('title'), this.getSlotText()].filter(Boolean).join('. ');
    }
    render() {
        const classes = this.composeClass('notification', {
            'with-icon': !!this.icon,
            [`${this.status}`]: !!this.status,
            [ItNotification_1.CLASS_NAME_SHOW]: this.isShown,
            [ItNotification_1.CLASS_NAME_FADE]: this.fade,
            dismissable: this.dismissable,
            [`${this.fix}-fix`]: this.fix,
        });
        const headingClasses = this.composeClass('notification-title', {
            'd-flex align-items-center': !!this.icon,
        });
        const headingTag = unsafeStatic(this.getHeadingLevel());
        return html `
      <div role="alert" class="visually-hidden" part="live-region"></div>
      <div class="${classes}" part="notification">
        <div aria-hidden="true">
          ${html$1 `
            <${headingTag} class=${headingClasses} id=${`${this._id}-heading`} part="title">
              ${this.icon
            ? html `<it-icon
                      class="icon me-2"
                      size="sm"
                      color=${ifDefined(this.status ? NOTIFICATION_STATUS_COLORS[this.status] : undefined)}
                      name="${this.icon}"
                      align="none"
                    ></it-icon>`
            : ''}<slot name="title"></slot>
            </${headingTag}>
          `}
          <slot></slot>
        </div>
        ${this.dismissable
            ? html `
              <button type="button" class="btn notification-close" @click=${this.hide}>
                <it-icon class="icon" name="it-close"></it-icon>
                <span class="visually-hidden">${this.$t('notification_close_label')}</span>
              </button>
            `
            : ''}
      </div>
    `;
    }
};
ItNotification.styles = styles;
ItNotification.CLASS_NAME_FADE = 'fade';
ItNotification.CLASS_NAME_SHOW = 'show';
ItNotification.TRANSITION_DURATION = 150;
__decorate([
    property({ type: String, attribute: 'heading-level' }),
    __metadata("design:type", String)
], ItNotification.prototype, "headingLevel", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItNotification.prototype, "icon", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItNotification.prototype, "status", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItNotification.prototype, "dismissable", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItNotification.prototype, "fade", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItNotification.prototype, "fix", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Number)
], ItNotification.prototype, "timeout", void 0);
__decorate([
    query('[part="notification"]'),
    __metadata("design:type", HTMLElement)
], ItNotification.prototype, "container", void 0);
__decorate([
    query('[part="live-region"]'),
    __metadata("design:type", HTMLElement)
], ItNotification.prototype, "liveRegion", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItNotification.prototype, "isShown", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItNotification.prototype, "isTransitioning", void 0);
ItNotification = ItNotification_1 = __decorate([
    customElement('it-notification')
], ItNotification);

export { ItNotification };
//# sourceMappingURL=it-notification.js.map
